import { ReactElement, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useRepositoriesContext } from "../../../contexts/RepositoriesContext";
import { ApiKeyListDto, KeyStatus } from "../../../data/clients/ExternalApiClient";
import { PageShell } from "./shared/PageShell";
import { KpiTile } from "./shared/KpiTile";
import { StatusPill } from "./shared/StatusPill";
import {
  formatDateTime,
  formatInt,
  formatMoney,
  formatRelative,
  planSummary,
} from "./shared/formatters";
import { PlanEditor } from "./components/PlanEditor";
import { ScopeGrid } from "./components/ScopeGrid";
import { UsageCharts } from "./components/UsageCharts";
import { BillingPeriodsTable } from "./components/BillingPeriodsTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faPause,
  faPlay,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

type Tab = "overview" | "plan" | "usage";

const RANGE_PRESETS = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

export function ApiKeyDetailScreen(): ReactElement {
  const { apiKeyRepository } = useRepositoriesContext();
  const { id: idParam } = useParams();
  const id = parseInt(idParam ?? "0", 10);

  const keys = apiKeyRepository.useKeys();
  const knownScopes = apiKeyRepository.useKnownScopes();
  const plan = apiKeyRepository.usePlan(id);
  const billing = apiKeyRepository.useBillingPeriods(id, 12);

  const key = (keys.data ?? []).find((k) => k.id === id);

  const [tab, setTab] = useState<Tab>("overview");
  const [rangeDays, setRangeDays] = useState(30);

  const { fromIso, toIso } = useMemo(() => {
    const to = new Date();
    const from = new Date(to.getTime() - rangeDays * 86400_000);
    return { fromIso: from.toISOString(), toIso: to.toISOString() };
  }, [rangeDays]);

  const usageDay = apiKeyRepository.useUsage(id, fromIso, toIso, "day");
  const usageScope = apiKeyRepository.useUsage(id, fromIso, toIso, "scope");

  const currentPeriod =
    billing.data?.periods.find((p) => p.isCurrent) ?? null;

  return (
    <PageShell
      eyebrow={
        <Link
          to="/admin/api-keys"
          className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-900"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="text-[9px]" />
          API-nøgler
        </Link>
      }
      title={key?.name ?? "Indlæser…"}
      subtitle={
        key && (
          <span className="font-mono text-xs text-slate-500">
            {key.keyIdPrefix} · forhandler {key.organizationId ?? "—"}
          </span>
        )
      }
      actions={
        key && (
          <KeyActions
            keyData={key}
            onChangeStatus={async (status) => {
              await apiKeyRepository.updateKeyStatus(key.id, status);
            }}
          />
        )
      }
    >
      {!key && !keys.isLoading && (
        <div className="bg-white border border-slate-200 rounded-sm px-5 py-12 text-center text-sm text-slate-500">
          API-nøgle ikke fundet.
        </div>
      )}

      {key && (
        <>
          <div className="flex border-b border-slate-200 mb-6">
            {(
              [
                { id: "overview", label: "Oversigt" },
                { id: "plan", label: "Plan & scopes" },
                { id: "usage", label: "Forbrug" },
              ] as { id: Tab; label: string }[]
            ).map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-4 py-2.5 text-xs font-medium uppercase tracking-wider transition-colors border-b-2 -mb-px ${
                  tab === t.id
                    ? "text-slate-900 border-slate-900"
                    : "text-slate-500 border-transparent hover:text-slate-700"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === "overview" && (
            <OverviewTab keyData={key} currentPeriod={currentPeriod} />
          )}

          {tab === "plan" && (
            <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
              <div className="xl:col-span-3">
                <PlanEditor
                  initial={plan.data ?? null}
                  availableScopes={(knownScopes.data ?? []).map((s) => s.scope)}
                  onSave={async (input) => {
                    await apiKeyRepository.upsertPlan(id, input);
                  }}
                />
              </div>
              <div className="xl:col-span-2">
                <ScopeGrid
                  knownScopes={knownScopes.data ?? []}
                  initialSelected={key.scopes}
                  onSave={async (scopes) => {
                    await apiKeyRepository.updateKeyScopes(id, scopes);
                  }}
                />
              </div>
            </div>
          )}

          {tab === "usage" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="text-xs text-slate-500">
                  Viser forbrug for de seneste {rangeDays} dage.
                </div>
                <div className="flex items-center gap-1 p-0.5 bg-slate-100 rounded-sm">
                  {RANGE_PRESETS.map((p) => (
                    <button
                      key={p.days}
                      onClick={() => setRangeDays(p.days)}
                      className={`px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider rounded-sm transition-colors ${
                        rangeDays === p.days
                          ? "bg-white text-slate-900 shadow-sm"
                          : "text-slate-500 hover:text-slate-700"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              <UsageCharts
                series={usageDay.data?.series ?? []}
                topRoutes={usageDay.data?.topRoutes ?? []}
                scopeBreakdown={usageScope.data?.series ?? []}
                loading={usageDay.isLoading}
              />

              <BillingPeriodsTable
                periods={billing.data?.periods ?? []}
                loading={billing.isLoading}
              />
            </div>
          )}
        </>
      )}
    </PageShell>
  );
}

function OverviewTab({
  keyData,
  currentPeriod,
}: {
  keyData: ApiKeyListDto;
  currentPeriod: {
    callsTotal: number;
    callsIncluded: number;
    callsOverage: number;
    total: number;
    currency: string;
    periodStart: string;
    periodEnd: string | null;
  } | null;
}): ReactElement {
  const usageRatio = currentPeriod
    ? currentPeriod.callsIncluded > 0
      ? Math.min(1, currentPeriod.callsTotal / currentPeriod.callsIncluded)
      : 0
    : 0;

  return (
    <div className="grid grid-cols-12 gap-6">
      <section className="col-span-12 xl:col-span-7 space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <KpiTile
            label="Kald i denne periode"
            value={
              <span>
                {formatInt(currentPeriod?.callsTotal ?? 0)}
                {currentPeriod && currentPeriod.callsIncluded > 0 && (
                  <>
                    <span className="text-slate-300 mx-1">/</span>
                    <span className="text-slate-400 text-xl">
                      {formatInt(currentPeriod.callsIncluded)}
                    </span>
                  </>
                )}
              </span>
            }
            hint={
              currentPeriod &&
              currentPeriod.callsOverage > 0 && (
                <span className="text-amber-700">
                  {formatInt(currentPeriod.callsOverage)} over inkluderet
                </span>
              )
            }
            accent={
              currentPeriod && currentPeriod.callsIncluded > 0 ? (
                <UsageRing ratio={usageRatio} />
              ) : null
            }
          />
          <KpiTile
            label="Periodens omkostning"
            value={
              currentPeriod
                ? formatMoney(currentPeriod.total, currentPeriod.currency)
                : "—"
            }
            hint={
              currentPeriod &&
              `${formatRelative(currentPeriod.periodStart)} → nu`
            }
          />
        </div>

        <div className="bg-white border border-slate-200 rounded-sm">
          <div className="px-5 py-3 border-b border-slate-200">
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
              Nøgledetaljer
            </div>
          </div>
          <dl className="divide-y divide-slate-100">
            <Row label="Status">
              <StatusPill status={keyData.status} />
            </Row>
            <Row label="Plan">{planSummary(keyData.plan)}</Row>
            <Row label="Forhandler-organisations-ID">
              <span className="font-mono text-xs">
                {keyData.organizationId ?? "—"}
              </span>
            </Row>
            <Row label="Nøgle-ID">
              <span className="font-mono text-xs">{keyData.keyIdPrefix}</span>
            </Row>
            <Row label="Oprettet">{formatDateTime(keyData.createdAt)}</Row>
            <Row label="Udløber">
              {keyData.expiresAt ? formatDateTime(keyData.expiresAt) : "Aldrig"}
            </Row>
            <Row label="Sidst brugt">{formatRelative(keyData.lastUsedAt)}</Row>
            <Row label="Scopes">
              <div className="flex flex-wrap gap-1 max-w-md">
                {keyData.scopes.length === 0 ? (
                  <span className="text-xs text-slate-400 italic">
                    Ingen scopes — nøglen kan ikke kalde nogen endpoints.
                  </span>
                ) : (
                  keyData.scopes.map((s) => (
                    <span
                      key={s}
                      className="px-1.5 py-0.5 text-[10px] font-mono text-slate-600 bg-slate-100 rounded-sm"
                    >
                      {s}
                    </span>
                  ))
                )}
              </div>
            </Row>
          </dl>
        </div>
      </section>

      <section className="col-span-12 xl:col-span-5">
        <div className="bg-white border border-slate-200 rounded-sm">
          <div className="px-5 py-3 border-b border-slate-200">
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
              Planoversigt
            </div>
          </div>
          <div className="px-5 py-5">
            <div className="font-mono text-2xl text-slate-900 tabular-nums tracking-tight">
              {planSummary(keyData.plan)}
            </div>
            {keyData.plan?.notes && (
              <div className="mt-3 text-xs text-slate-500 italic border-l-2 border-slate-200 pl-3">
                {keyData.plan.notes}
              </div>
            )}
            <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
              <span className="text-slate-500">Type</span>
              <span className="font-mono text-slate-900">
                {keyData.plan?.planType ?? "—"}
              </span>
              {keyData.plan?.billingPeriod && (
                <>
                  <span className="text-slate-500">Periode</span>
                  <span className="font-mono text-slate-900">
                    {keyData.plan.billingPeriod}
                  </span>
                </>
              )}
              {keyData.plan?.includedCalls != null && (
                <>
                  <span className="text-slate-500">Inkluderet</span>
                  <span className="font-mono text-slate-900 tabular-nums">
                    {formatInt(keyData.plan.includedCalls)}
                  </span>
                </>
              )}
              {keyData.plan?.baseFee != null && (
                <>
                  <span className="text-slate-500">Grundgebyr</span>
                  <span className="font-mono text-slate-900 tabular-nums">
                    {formatMoney(
                      keyData.plan.baseFee,
                      keyData.plan.currency,
                    )}
                  </span>
                </>
              )}
              {keyData.plan?.overageRate != null && (
                <>
                  <span className="text-slate-500">Overforbrug</span>
                  <span className="font-mono text-slate-900 tabular-nums">
                    {keyData.plan.overageRate} {keyData.plan.currency} / kald
                  </span>
                </>
              )}
              {keyData.plan?.scopeOverrides &&
                keyData.plan.scopeOverrides.length > 0 && (
                  <>
                    <span className="text-slate-500">Overrides</span>
                    <span className="font-mono text-slate-900">
                      {keyData.plan.scopeOverrides.length}
                    </span>
                  </>
                )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): ReactElement {
  return (
    <div className="grid grid-cols-[180px_1fr] items-center gap-4 px-5 py-3">
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="text-xs text-slate-900">{children}</dd>
    </div>
  );
}

function UsageRing({ ratio }: { ratio: number }): ReactElement {
  const r = 14;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - ratio);
  const tone =
    ratio >= 1
      ? "rgb(225 29 72)"
      : ratio >= 0.85
        ? "rgb(234 88 12)"
        : "rgb(15 23 42)";
  return (
    <svg width="36" height="36" className="-mt-1 -mr-1">
      <circle
        cx="18"
        cy="18"
        r={r}
        stroke="rgb(241 245 249)"
        strokeWidth="3"
        fill="none"
      />
      <circle
        cx="18"
        cy="18"
        r={r}
        stroke={tone}
        strokeWidth="3"
        fill="none"
        strokeDasharray={c}
        strokeDashoffset={offset}
        transform="rotate(-90 18 18)"
        strokeLinecap="round"
      />
      <text
        x="18"
        y="22"
        textAnchor="middle"
        fontSize="10"
        fontFamily="ui-monospace, SFMono-Regular, monospace"
        fill="rgb(15 23 42)"
      >
        {Math.round(ratio * 100)}%
      </text>
    </svg>
  );
}

function KeyActions({
  keyData,
  onChangeStatus,
}: {
  keyData: ApiKeyListDto;
  onChangeStatus: (status: KeyStatus) => Promise<void>;
}): ReactElement {
  const [busy, setBusy] = useState(false);

  async function setStatus(status: KeyStatus) {
    setBusy(true);
    try {
      await onChangeStatus(status);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center gap-2">
      <StatusPill status={keyData.status} />
      {keyData.status === "Active" ? (
        <button
          disabled={busy}
          onClick={() => setStatus("Blocked")}
          className="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-semibold text-rose-700 hover:text-white hover:bg-rose-600 border border-rose-300 hover:border-rose-600 rounded-sm transition-colors disabled:opacity-50"
        >
          <FontAwesomeIcon icon={faPause} className="text-[10px]" />
          Blokér nøgle
        </button>
      ) : (
        <button
          disabled={busy}
          onClick={() => setStatus("Active")}
          className="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-semibold text-emerald-700 hover:text-white hover:bg-emerald-600 border border-emerald-300 hover:border-emerald-600 rounded-sm transition-colors disabled:opacity-50"
        >
          <FontAwesomeIcon icon={faPlay} className="text-[10px]" />
          Aktivér
        </button>
      )}
      {keyData.status !== "Expired" && (
        <button
          disabled={busy}
          onClick={() => setStatus("Expired")}
          className="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-400 rounded-sm transition-colors disabled:opacity-50"
        >
          <FontAwesomeIcon icon={faXmark} className="text-[10px]" />
          Markér som udløbet
        </button>
      )}
    </div>
  );
}
